<script setup lang="ts">
  import { onMounted, onUnmounted, computed, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useModal } from 'vue-final-modal';
  import { search, type Department } from '@/entities/Department';
  import { CreateDepartmentModal } from '../index';
  import DepartmentsTable from './DepartmentsTable.vue';
  import EditDepartmentModal from './EditDepartmentModal.vue';
  import AdminListPageLayout from '../AdminListPageLayout.vue';
  import {
    useAdminListPageSetup,
    createAdminListPageProps,
    useItemSelection,
    useDepartmentDelete,
    useDepartmentPositionManagement,
    useDepartmentManagement,
  } from '../../lib';
  import { useDepartmentsExport } from './useDepartmentsExport';

  const props = defineProps(createAdminListPageProps());

  const { createDepartmentWithPositions } = useDepartmentManagement();

  const {
    items: departments,
    allItems,
    hasMoreItems,
    loadingMore,
    isShowAllMode,
    isInfiniteScrollMode,
    loading,
    handleSearch,
    initSearch,
    metadata,
    currentPage,
    selectedQuantity,
    onPageChanged,
    handleQuantityChange,
    searchTerm,
    cleanup,
    handleSortOptions,
    isUsingFilters,
    handleTableFilter,
    sort,
    order,
  } = useAdminListPageSetup<Department>(
    {
      defaultSearchUrl: '/departments',
      searchFunction: search,
      defaultAmount: 10,
      skipShowAllPrefetch: true,
    },
    props
  );

  const {
    selectedCount,
    hasSelected,
    isSelected,
    toggleSelection,
    selectedItems,
    deselectAll,
    toggleAll,
  } = useItemSelection<Department>();

  const departmentsList = computed<Department[]>(() =>
    isShowAllMode.value ? (allItems.value ?? []) : (departments.value ?? [])
  );

  const allSelected = computed(
    () =>
      departmentsList.value.length > 0 &&
      departmentsList.value.every((dept: Department) => isSelected(dept.id))
  );

  const handleToggleAll = () => {
    toggleAll(departmentsList.value);
  };

  const autoSelectedIds = ref<number[]>([]);

  const route = useRoute();
  const router = useRouter();

  const refreshList = async () => {
    if (isShowAllMode.value) {
      currentPage.value = 1;
      if (route.query.page !== undefined) {
        const rest = { ...route.query };
        delete rest.page;
        await router.replace({ query: rest });
      }
    }
    await initSearch();
  };

  const { openDeleteConfirmationModal, showNoSelectionError } = useDepartmentDelete(
    () => selectedCount.value,
    () => selectedItems.value,
    async (_deletedIds: number[]) => {
      await refreshList();
    },
    (_ids: number[]) => {
      deselectAll();
    },
    () => {
      autoSelectedIds.value.forEach((id) => {
        if (isSelected(id)) {
          toggleSelection(id);
        }
      });
      autoSelectedIds.value = [];
    }
  );

  const handleBulkDelete = (ids: number[]) => {
    if (ids.length === 0) {
      showNoSelectionError();
      return;
    }
    autoSelectedIds.value = [];
    openDeleteConfirmationModal();
  };

  const totalItems = computed(() => metadata.value?.total ?? 0);

  const itemsPerPage = computed({
    get: () => selectedQuantity.value,
    set: (val: number) => {
      if (val > 0 && val <= 100) {
        handleQuantityChange(val);
      }
    },
  });

  const handlePageChange = (page: number) => {
    onPageChanged(page);
  };

  const selectedDepartment = ref<Department | null>(null);

  const { updateDepartmentPositions } = useDepartmentPositionManagement();

  const { open: openCreateModal, close: closeCreateModal } = useModal({
    component: CreateDepartmentModal,
    attrs: {
      title: 'Novo Departamento',
      onClose: () => closeCreateModal(),
      onSave: async (data: { code: string; positionIds: number[] }) => {
        const { success } = await createDepartmentWithPositions(data.code, data.positionIds);
        if (success) {
          await refreshList();
          closeCreateModal();
        }
      },
    },
  });

  const editDepartmentModal = useModal({
    component: EditDepartmentModal,
    attrs: {
      get title() {
        return 'Editar Departamento';
      },
      get department() {
        return selectedDepartment.value;
      },
      onClose() {
        editDepartmentModal.close();
        selectedDepartment.value = null;
      },
      async onSave(data: { addPositions: number[]; removePositions: number[]; code?: string }) {
        if (!selectedDepartment.value) return;

        const { success } = await updateDepartmentPositions(
          selectedDepartment.value.id,
          data.addPositions,
          data.removePositions,
          data.code
        );

        if (success) {
          editDepartmentModal.close();
          selectedDepartment.value = null;
          await refreshList();
        }
      },
    },
  });

  const { handleExportToExcel } = useDepartmentsExport({
    searchTerm,
    sort,
    order,
  });

  const handleCreateDepartment = () => {
    openCreateModal();
  };

  const handleEdit = (id: number) => {
    const department = departmentsList.value.find((d: Department) => d.id === id);
    if (department) {
      selectedDepartment.value = department;
      editDepartmentModal.open();
    }
  };

  const handleCopy = (_id: number) => {
    // TODO: Implementar funcionalidade de clonar departamento
  };

  const handleInfo = (_id: number) => {
    // TODO: Implementar modal/drawer de informações do departamento
  };

  const handleDelete = async (id: number) => {
    deselectAll();
    toggleSelection(id);
    autoSelectedIds.value = [id];
    await openDeleteConfirmationModal();
  };

  onMounted(async () => {
    await initSearch();
  });

  onUnmounted(() => {
    cleanup();
  });
</script>

<template>
  <div
    data-testid="admin-departments-page"
    :data-total-items="totalItems"
  >
    <AdminListPageLayout
      v-model:selected-quantity="itemsPerPage"
      title="Departamentos"
      search-placeholder="Buscar"
      create-button-text="Novo Departamento"
      entity-name="departamento"
      empty-state-message="Nenhum departamento cadastrado"
      :items="departmentsList"
      :loading="loading"
      :search-term="searchTerm"
      :metadata="metadata"
      :is-using-filters="isUsingFilters"
      :current-page="currentPage"
      @page-changed="handlePageChange"
      @search="handleSearch"
      @create="handleCreateDepartment"
      @export="handleExportToExcel"
    >
      <template #table>
        <DepartmentsTable
          :departments="departmentsList"
          :loading="loading"
          :is-filter-active="false"
          :search-term="searchTerm"
          :selected-count="selectedCount"
          :has-selected="hasSelected"
          :is-selected="isSelected"
          :selected-items="selectedItems"
          :all-selected="allSelected"
          :on-toggle-selection="toggleSelection"
          :on-toggle-all="handleToggleAll"
          :on-open-delete-confirmation="openDeleteConfirmationModal"
          :on-show-no-selection-error="showNoSelectionError"
          @edit="handleEdit"
          @copy="handleCopy"
          @delete="handleDelete"
          @update:sort="handleSortOptions"
          @update:filter="handleTableFilter"
        />
      </template>
    </AdminListPageLayout>
    <div
      v-if="isInfiniteScrollMode"
      id="infinite-scroll-trigger"
      class="py-4 text-center"
      role="status"
      :aria-live="loadingMore ? 'polite' : 'off'"
      :aria-busy="loadingMore"
    >
      <div
        v-if="loadingMore"
        class="py-2 text-gray-03"
      >
        <span class="inline-block animate-spin mr-2">⟳</span>
        Carregando mais departamentos...
      </div>
      <div
        v-else-if="!hasMoreItems && departmentsList.length > 0"
        class="py-2 text-gray-03"
      >
        Não há mais departamentos para carregar
      </div>
    </div>
  </div>
</template>
